import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { db } from '../firebase/config';
import { doc, getDoc } from 'firebase/firestore';

/**
 * WorkbookVideoPlayer
 *
 * Renders the day video at the top of a workbook day page. When the user
 * scrolls past it, a mini-player appears in the bottom right corner.
 * Scrolling back up returns the player to its inline position.
 *
 * Implementation: A single iframe is rendered inside a React Portal that
 * lives outside the normal component tree (mounted to document.body).
 * That iframe is absolutely positioned over an "anchor slot" (either the
 * inline placeholder or the mini-player placeholder) using getBoundingClientRect
 * to read each slot's screen position. Because the iframe is never unmounted
 * or moved through the DOM tree, playback continues uninterrupted.
 *
 * Access logic: shows only if videos/day{N} is active AND
 * (engagement.videosEnabled OR user.videoAccess includes N).
 *
 * If no video exists or access is denied, renders nothing.
 */
export default function WorkbookVideoPlayer({ dayNumber, user }) {
  const [video, setVideo] = useState(null);
  const [hasAccess, setHasAccess] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showMini, setShowMini] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [iframePos, setIframePos] = useState({ top: 0, left: 0, width: 0, height: 0 });

  const inlineSlotRef = useRef(null);
  const miniSlotRef = useRef(null);
  const portalNodeRef = useRef(null);

  // ====== LOAD VIDEO + ACCESS ======
  useEffect(() => {
    const load = async () => {
      try {
        const [videoSnap, userSnap] = await Promise.all([
          getDoc(doc(db, 'videos', `day${dayNumber}`)),
          getDoc(doc(db, 'users', user.uid)),
        ]);

        if (!videoSnap.exists() || !videoSnap.data().active || !videoSnap.data().embedUrl) {
          setLoading(false);
          return;
        }
        setVideo(videoSnap.data());

        if (!userSnap.exists()) {
          setLoading(false);
          return;
        }
        const profile = userSnap.data();
        const userVideoAccess = profile.videoAccess || [];
        // Match the field-name convention used elsewhere in the portal
        // (see AccountSettings.js). Newer user docs use companyCode,
        // older docs may use engagementId. Try both.
        const engagementCode = profile.companyCode || profile.engagementId;

        let cohortEnabled = false;
        if (engagementCode) {
          const engSnap = await getDoc(doc(db, 'engagements', engagementCode));
          if (engSnap.exists()) {
            cohortEnabled = !!engSnap.data().videosEnabled;
          }
        }
        setHasAccess(cohortEnabled || userVideoAccess.includes(dayNumber));
      } catch (err) {
        console.error('WorkbookVideoPlayer load error:', err);
      }
      setLoading(false);
    };
    load();
  }, [dayNumber, user.uid]);

  // ====== CREATE PORTAL TARGET ON document.body ======
  useEffect(() => {
    if (!video || !hasAccess) return;
    const node = document.createElement('div');
    node.id = `wbvp-portal-day${dayNumber}`;
    document.body.appendChild(node);
    portalNodeRef.current = node;
    return () => {
      if (node.parentNode) node.parentNode.removeChild(node);
      portalNodeRef.current = null;
    };
  }, [video, hasAccess, dayNumber]);

  // ====== WATCH INLINE SLOT VISIBILITY FOR MINI TOGGLE ======
  useEffect(() => {
    if (!video || !hasAccess || dismissed) return;
    if (!inlineSlotRef.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => setShowMini(!entry.isIntersecting),
      { threshold: 0, rootMargin: '0px' }
    );
    observer.observe(inlineSlotRef.current);
    return () => observer.disconnect();
  }, [video, hasAccess, dismissed]);

  // ====== UPDATE IFRAME POSITION TO MATCH ACTIVE SLOT ======
  useEffect(() => {
    if (!video || !hasAccess || dismissed) return;

    const updatePosition = () => {
      const targetRef = showMini ? miniSlotRef.current : inlineSlotRef.current;
      if (!targetRef) return;
      const rect = targetRef.getBoundingClientRect();
      setIframePos({
        top: rect.top + window.scrollY,
        left: rect.left + window.scrollX,
        width: rect.width,
        height: rect.height,
      });
    };

    updatePosition();
    // Update on scroll, resize, and any layout shift
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    // Run again on next frame in case layout settled after state change
    const raf = requestAnimationFrame(updatePosition);

    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
      cancelAnimationFrame(raf);
    };
  }, [showMini, video, hasAccess, dismissed]);

  const handleDismiss = () => {
    setDismissed(true);
    setShowMini(false);
  };

  if (loading) return null;
  if (!video || !hasAccess || dismissed) return null;

  return (
    <>
      {/* INLINE ANCHOR: empty box at natural position in the page. The portal iframe overlays it. */}
      <div style={s.inlineWrap}>
        <div style={s.inlinePlayer}>
          <div style={s.headerBar}>
            <span style={s.headerLabel}>Day {dayNumber} Video</span>
          </div>
          <div ref={inlineSlotRef} style={s.videoFrame} />
        </div>
      </div>

      {/* MINI ANCHOR: invisible until showMini is true */}
      {showMini && (
        <div style={s.miniWrap}>
          <div style={s.miniHeader}>
            <span style={s.miniLabel}>Day {dayNumber} Video</span>
            <button
              style={s.dismissBtn}
              onClick={handleDismiss}
              aria-label="Close mini player"
            >
              ×
            </button>
          </div>
          <div ref={miniSlotRef} style={s.miniFrame} />
        </div>
      )}

      {/* PORTAL IFRAME: rendered once, positioned absolutely over whichever slot is active */}
      {portalNodeRef.current && ReactDOM.createPortal(
        <iframe
          src={video.embedUrl}
          style={{
            position: 'absolute',
            top: `${iframePos.top}px`,
            left: `${iframePos.left}px`,
            width: `${iframePos.width}px`,
            height: `${iframePos.height}px`,
            border: 'none',
            zIndex: showMini ? 9000 : 5,
            boxShadow: showMini ? '0 12px 32px rgba(27, 42, 74, 0.35)' : 'none',
            transition: 'box-shadow 0.2s',
          }}
          frameBorder="0"
          allow="autoplay; fullscreen; picture-in-picture"
          allowFullScreen
          title={`Day ${dayNumber} Video`}
        />,
        portalNodeRef.current
      )}
    </>
  );
}

const s = {
  inlineWrap: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: '20px',
  },
  inlinePlayer: {
    width: '360px',
    maxWidth: '100%',
    boxShadow: '0 4px 16px rgba(27, 42, 74, 0.12)',
  },
  headerBar: {
    background: '#1B2A4A',
    padding: '8px 14px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  headerLabel: {
    color: '#D4A847',
    fontFamily: 'Playfair Display, serif',
    fontSize: '12px',
    fontWeight: '700',
    letterSpacing: '1px',
    textTransform: 'uppercase',
  },
  videoFrame: {
    position: 'relative',
    width: '100%',
    paddingTop: '56.25%',
    background: '#000000',
  },

  miniWrap: {
    position: 'fixed',
    bottom: '24px',
    right: '24px',
    width: '280px',
    zIndex: 8999,
    boxShadow: '0 12px 32px rgba(27, 42, 74, 0.35)',
  },
  miniHeader: {
    background: '#1B2A4A',
    padding: '6px 12px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    position: 'relative',
    zIndex: 9001,
  },
  miniLabel: {
    color: '#D4A847',
    fontFamily: 'Playfair Display, serif',
    fontSize: '11px',
    fontWeight: '700',
    letterSpacing: '1px',
    textTransform: 'uppercase',
  },
  dismissBtn: {
    background: 'none',
    border: 'none',
    color: '#FFFFFF',
    fontSize: '20px',
    fontWeight: '300',
    cursor: 'pointer',
    lineHeight: '1',
    padding: '0 2px',
    fontFamily: 'DM Sans, sans-serif',
  },
  miniFrame: {
    position: 'relative',
    width: '100%',
    paddingTop: '56.25%',
    background: '#000000',
  },
};
